import { OccurrenceWorkingStatus } from "interfaces/Occurrence";
import { ICoord } from "services/CordsService";
import { PolicemanMapMarker } from "utils/treatSocketLocations";
import { IFilterCount, IFilterOptions, IMapLocationData, MapLocationType } from "./types";

export async function handleFavoriteMarkerStorage(locationId: string, isFavorite: boolean) {
  const localStorageIds = localStorage.getItem('@preser-app/favoriteMarkers') || null;
  if (localStorageIds) {
    const favoriteMarkers = JSON.parse(localStorageIds) as string[];

    if (!favoriteMarkers.includes(locationId) && isFavorite) {
      favoriteMarkers.push(locationId);
    } else if (favoriteMarkers.includes(locationId) && !isFavorite) {
      const favoriteIndex = favoriteMarkers.indexOf(locationId);
      favoriteMarkers.splice(favoriteIndex, 1);
    }

    return localStorage.setItem('@preser-app/favoriteMarkers', JSON.stringify(favoriteMarkers))
  } 

  localStorage.setItem('@preser-app/favoriteMarkers', JSON.stringify([
    locationId
  ]))
}

export async function mountOccurrencesLocationMarker(locations: ICoord[]) {
  let favoriteMarkersIds: string[] = [];

  const localIds = localStorage.getItem('@preser-app/favoriteMarkers') || null;
  if (localIds) {
    favoriteMarkersIds = JSON.parse(localIds || '');
  }

  const countValues: IFilterCount = {
    occPatrimonial: 0,
    occVehicle: 0,
    policeman: 0,
    policemanOnDuty: 0,
  }

  const occurrencesMarkers: IMapLocationData[] = locations.map((occ) => {
    const type: keyof IFilterOptions = 
      occ.patrimonialType === 2 ? 'occVehicle' : 'occPatrimonial';
    countValues[type] += 1;

    return {
      dataId: occ.id,
      name: occ.patrimonialType === 2 ? 'Veicular' : 'Patrimonial',
      type: occ.patrimonialType === 2 ? 'vehicle' : 'patrimony',
      coords: {
        lat: occ.coords.x,
        lng: occ.coords.y,
      },
      working: occ.status && OccurrenceWorkingStatus.includes(occ.status) ? true : false,
      isFavorite: favoriteMarkersIds.includes(occ.id),
      filterType: type,
      searchRadius: occ.searchDistance
    } as IMapLocationData;
  })

  return { occurrencesMarkers, countValues };
}

export function mountPolicemanLocationMarker(locations: PolicemanMapMarker[]) {
  const policemanMarkers: IMapLocationData[] = locations.map((policeman) => ({
    dataId: policeman.id,
    name: policeman.name ?? '--',
    type: policeman.working ? 'working-policeman' : 'free-policeman',
    coords: {
      lat: policeman.coords.latitude,
      lng: policeman.coords.longitude
    },
    filterType: policeman.working ? 'policemanOnDuty' : 'policeman',
    working: policeman.working,
    searchRadius: 30000,
  }))

  return policemanMarkers;
}

export const hasStringMatched = (text: string, searchString?: string | undefined) => {
  if (!searchString) return false;
  return text.toLowerCase().includes(searchString.toLowerCase())
};

type IsMarkerSearchFullfilledParams = {
  dataId: string;
  name: string;
  searchString: string;
}

export const isMarkerSearchFullfilled = ({
  dataId,
  name,
  searchString
}: IsMarkerSearchFullfilledParams) => {
  return (hasStringMatched(searchString, dataId) || hasStringMatched(searchString, name))
}

type FilterAllMarkersParams = {
  markers: IMapLocationData[];
  markerFilters: IFilterOptions;
  searchString?: string;
}

export function findBySearchOrFilter({
  markerFilters,
  markers,
  searchString
}: FilterAllMarkersParams) {
  if (markers === undefined || markers.length === 0) return [];

  const isVehicleFilterActive = (type: MapLocationType) => type === 'vehicle' && markerFilters.occVehicle.isActive;
  const isPatrimonyFilterActive = (type: MapLocationType) => type === 'patrimony' && markerFilters.occPatrimonial.isActive;
  const isFreePolicemanActive = (type: MapLocationType) => type === 'free-policeman' && markerFilters.policeman.isActive;
  const isDutyPolicemanActive = (type: MapLocationType) => type === 'working-policeman' && markerFilters.policemanOnDuty.isActive;

  if (!searchString) {
    return markers.filter((el) => 
      isVehicleFilterActive(el.type) || 
      isPatrimonyFilterActive(el.type) || 
      isFreePolicemanActive(el.type) || 
      isDutyPolicemanActive(el.type)
    )
  }

  return markers.filter((el) => {
    const isSearchMatched = isMarkerSearchFullfilled({
      dataId: el.dataId,
      name: el.name,
      searchString
    })

    return (
      (isVehicleFilterActive(el.type) && isSearchMatched) || 
      (isPatrimonyFilterActive(el.type) && isSearchMatched) || 
      (isFreePolicemanActive(el.type) && isSearchMatched) || 
      (isDutyPolicemanActive(el.type) && isSearchMatched)
    )
  })
}